"use client";
import { useToast } from "@/hooks/use-toast";
import axios from "axios";
import { useAtomValue } from "jotai";
import Cookies from "js-cookie";
import { useLocale, useTranslations } from "next-intl";
import { useParams, usePathname } from "next/navigation";
import { routePattern } from "./page-key";
import { useMemo } from "react";
import { userDataAtom } from "./atoms";
import { global } from "./global";
import { errorsParserv3, generateClientId } from "./helpers";
import { Store } from "./types";

export const getLSStoreData = () => {
  if (typeof window === "undefined") return;
  try {
    const storeData = JSON.parse(localStorage.getItem("storeData") ?? "{}");
    if (storeData?.token && storeData?.site && storeData?.company) return storeData as Store;
    return undefined;
  } catch (error) {
    return undefined;
  }
};

export const getLSClientId = () => {
  if (typeof window === "undefined") return "";
  try {
    let clientId = localStorage.getItem("clientId");

    if (!clientId) {
      clientId = generateClientId();
      localStorage.setItem("clientId", clientId as string);
    }

    return clientId;
  } catch (error) {
    console.log("clientId error", error);
    return "";
  }
};

export const getStoreToken = () => {
  return getLSStoreData()?.token;
};

export const getSiteToken = () => {
  if (global.site_token !== global.aysoft_token) return global.site_token;
  const site = getLSStoreData()?.site;
  if (site) return site;
  return global.site_token;
};

export const getCompanyToken = () => {
  if (global.site_token !== global.aysoft_token) return global.company_token;
  const company = getLSStoreData()?.company;
  if (company) return company;
  return global.company_token;
};

export const glob = global;

/**
 * Backend'in istemciyi tanıması için her isteğe eklenen sürüm header'ları.
 * Sürüm build anında next.config.ts'te üretiliyor. Bkz. docs/app-version-headers.md
 */
export const appHeaders = () => ({
  "X-App-Version": process.env.NEXT_PUBLIC_APP_VERSION,
  "X-App-Platform": "web",
  "X-App-Client": "aysoft",
});

/**
 * Oturum cookie'si bayrakları: HTTPS'te `secure`, çapraz site isteklerde
 * gönderilmesin diye `sameSite: lax` (strict, Stripe dönüşü gibi dış
 * yönlendirmelerde middleware'in cookie'yi görmesini engellerdi).
 */
export const authCookieFlags = () => ({
  sameSite: "lax" as const,
  secure: typeof window !== "undefined" && window.location.protocol === "https:",
});

type Body = Record<string, string | number | object | boolean | any[] | null | undefined> | undefined | FormData;

// Backend her response'a `x-request-id` header'ı basıyor. Hata durumunda bunu
// alert içinde kopyalanabilir şekilde gösterebilmek için error objesine
// taşıyoruz. Not: tarayıcının bu header'ı okuyabilmesi için backend'in
// `Access-Control-Expose-Headers: x-request-id` göndermesi gerekir.
const getRequestId = (error: any): string | undefined => {
  const headers = error?.response?.headers;
  if (!headers) return undefined;
  try {
    if (typeof headers.get === "function") return headers.get("x-request-id") || undefined;
    return headers["x-request-id"] || headers["X-Request-Id"] || undefined;
  } catch {
    return undefined;
  }
};

// -----------------------------------------------------------------------------
// JWT refresh — 401 interceptor tarafından çağrılan yardımcı
//
// Backend'te rolling JWT mimarisi var: `auth/user` her çağrıda yeni jwt dönüyor,
// `auth/refresh` ise mevcut (süresi dolmuş olabilecek) access token'ı 14 günlük
// refresh penceresi içinde yeniden uzatıyor. Frontend eskiden 401 alınca direkt
// logout ediyordu; şimdi önce /auth/refresh deniyor, başarılıysa isteği yeni
// token ile retry ediyor, başarısızsa 401'i yukarı (auth.ts) fırlatıp logout
// akışının çalışmasına izin veriyor.
//
// Concurrent 401'lerde singleflight: birden fazla istek aynı anda 401 alırsa
// sadece bir refresh çağrısı yapılır, diğerleri aynı promise'i bekler.
// -----------------------------------------------------------------------------
let refreshInflight: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    try {
      const current = Cookies.get(glob.authData.authorizationKey);
      if (!current) return null;
      const resp = await axios.post(
        `${glob.api_url}${glob.authData.refresh}`,
        {},
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + current,
            ClientId: getLSClientId(),
            AysoftToken: glob.aysoft_token,
            SiteToken: getSiteToken(),
            CompanyToken: getCompanyToken(),
            ...appHeaders(),
          },
        },
      );
      const newJwt = resp?.data?.access_token ?? null;
      if (newJwt) {
        Cookies.set(glob.authData.authorizationKey, newJwt, { expires: glob.authData.authorizationExpires, ...authCookieFlags() });
      }
      return newJwt;
    } catch {
      return null;
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

export function useApi(noAlert?: boolean) {
  const { toast } = useToast();
  const t = useTranslations("general");
  // Ekranın dili: cookie yoksa next-intl tarayıcı dilinden seçiyor, glob.locale ise
  // "de"ye düşüyordu; Türkçe ekranda backend mesajları Almanca geliyordu (5 Eki 2026).
  const locale = useLocale();
  const user = useAtomValue(userDataAtom);
  const pathname = usePathname();
  const routeParams = useParams();
  const isAdminPath = pathname.startsWith("/Admin");
  // Site bazlı yetki (9 Eki 2026): backend hangi sayfanın hangi ucu çağırdığını
  // öğrenir (PageApiLearner). Değer rota kalıbı, token içermez.
  const pagePattern = useMemo(() => routePattern(pathname, routeParams as Record<string, string | string[]>), [pathname, routeParams]);

  const apiRequest = useMemo(
    () => {
      const run = async (url: string, method = "GET", body: Body = undefined, extHeaders: Record<string, any> = {}, file = false, __isRetry = false): Promise<any> => {
        try {
          const BEARER_TOKEN = Cookies.get(glob.authData.authorizationKey) ? "Bearer " + Cookies.get(glob.authData.authorizationKey) : "";
          const endpoint = `${glob.api_url}${url}`;
          const { withStatus, ...headersWithoutFlags } = extHeaders;
          const headerList = {
            "Content-Type": Object.prototype.toString.call(body) === "[object FormData]" ? "multipart/form-data" : "application/json",
            Accept: "application/json",
            CompanyToken: extHeaders.public ? global.company_token : getCompanyToken(),
            SiteToken: extHeaders.public ? global.site_token : getSiteToken(),
            StoreToken: extHeaders.public ? undefined : getStoreToken(),
            AysoftToken: glob.aysoft_token,
            ...appHeaders(),
            Locale: locale,
            Authorization: BEARER_TOKEN,
            ClientId: getLSClientId(),
            "X-Page": pagePattern,
            ...headersWithoutFlags,
            ...(user?.demcas_user && isAdminPath && { demcastoken: "dklMCdwS", storeToken: undefined }),
          };

          const response = await axios({
            method,
            url: endpoint,
            headers: headerList,
            data: body,
            responseType: file ? "arraybuffer" : undefined,
            signal: extHeaders.signal || undefined,
          });

          // withStatus: 2xx içindeki özel kodları (ör. eSIM 216 "işlem sürüyor")
          // ayırt etmesi gereken çağrılar için HTTP kodu da döner.
          if (withStatus) return { httpStatus: response.status, body: response.data };
          return response.data;
        } catch (error: any) {
          // 401 interceptor: access token süresi dolmuşsa /auth/refresh deneyip
          // orijinal isteği yeni token ile bir kez retry et. Sonsuz döngüyü
          // önlemek için __isRetry bayrağı ve refresh/logout endpoint'lerini
          // bu mekanizmanın dışında tutuyoruz. Refresh başarısızsa 401'i
          // normal akışa bırakıyoruz (auth.ts logout'u tetikler).
          const status = error?.response?.status;
          const isAuthEndpoint = url === glob.authData.refresh || url === glob.authData.login || url === glob.authData.logout;
          if (status === 401 && !__isRetry && !isAuthEndpoint) {
            const newJwt = await performRefresh();
            if (newJwt) {
              return run(url, method, body, extHeaders, file, true);
            }
          }

          const requestId = getRequestId(error);
          let errors = error;
          if (error?.response?.data) {
            errors = {
              code: error?.response?.status,
              status: error?.response?.data?.status,
              message: error?.response?.data?.message,
              errors: error?.response?.data?.errors,
              data: error?.response?.data,
              requestId,
            };
          } else if (error?.request) {
            errors = {
              status: false,
              code: error?.request?.status,
              message: error?.request?.statusText || "Der Vorgang ist fehlgeschlagen.",
              requestId,
            };
          } else {
            errors = {
              status: error?.status,
              message: error?.message || "Der Vorgang ist fehlgeschlagen.",
              requestId,
            };
          }
          if ((errors.status === 500 || (!errors.status && errors.code !== 415 && errors.code !== 455)) && !noAlert) toast({ variant: "destructive", ...errorsParserv3(errors, t) });
          throw errors;
        }
      };
      // Dışarıya açılan fonksiyonda __isRetry parametresi gizli kalsın — call-site'lar
      // 4-parametreli imzayı kullanmaya devam ediyor.
      return (url: string, method = "GET", body: Body = undefined, extHeaders: Record<string, any> = {}, file = false) => run(url, method, body, extHeaders, file, false);
    },
    [noAlert, t, locale, user?.demcas_user, isAdminPath, toast, pagePattern],
  );

  const $get = useMemo(() => (url: string, headers?: Record<string, any>, file?: boolean) => apiRequest(url, "GET", undefined, headers, file), [apiRequest]);
  const $post = useMemo(() => (url: string, body: Body, headers?: Record<string, any>) => apiRequest(url, "POST", body, headers), [apiRequest]);
  const $put = useMemo(() => (url: string, body: Body, headers?: Record<string, any>) => apiRequest(url, "PUT", body, headers), [apiRequest]);
  const $del = useMemo(() => (url: string, body?: Body, headers?: Record<string, any>) => apiRequest(url, "DELETE", body, headers), [apiRequest]);

  return { $get, $post, $put, $del };
}

export async function exportBalance(data: { api: string; file_format: string; file_name: string }) {
  const bearerToken = Cookies.get("Authorization") ? "Bearer " + Cookies.get("Authorization") : "";

  await axios(data.api, {
    baseURL: glob.api_url,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      CompanyToken: glob.company_token,
      Demcastoken: "dklMCdwS",
      Locale: "de",
      Authorization: bearerToken,
      ...appHeaders(),
    },
    method: "get",

    withCredentials: true,
    responseType: "arraybuffer",
  })
    .then((response) => {
      const blob = new Blob([response.data], {
        type: data.file_format === "pdf" ? "application/pdf" : "application/xls",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = data.file_name + "." + data.file_format;
      link.click();
    })
    .catch((err) => {
      throw err;
    });
}

export async function exportInvoice(invoice: any, format: any, detail: any, invoiceType: string, summary?: any) {
  let link = `invoice-show/${invoice.token}?export=${format}&${invoiceType}=${invoice[invoiceType]}`;

  if (detail !== undefined && detail !== null) {
    link += `&detail=${detail}`;
  }

  if (summary !== undefined && summary !== null) {
    link += `&summary=${summary}`;
  }

  const bearerToken = Cookies.get("Authorization") ? "Bearer " + Cookies.get("Authorization") : "";

  const respo = await axios(link, {
    baseURL: glob.api_url,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      CompanyToken: glob.company_token,
      Demcastoken: "dklMCdwS",
      Locale: "de",
      Authorization: bearerToken,
      ...appHeaders(),
    },
    method: "get",
    withCredentials: true,
    responseType: "arraybuffer",
  })
    .then((response) => {
      const blob = new Blob([response.data], {
        type: format === "pdf" ? "application/pdf" : "application/xls",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = invoice.invoice_no + "." + format;
      link.click();
    })
    .catch((err) => {
      throw err.response.data;
    })
    .finally(() => {});
}
